import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isEqual, keys, mapObject, extend, noop, isObject, first } from 'underscore';
import { Promise } from 'bluebird';

import {
  DefaultButton, Panel, Spinner, PanelType, MessageBar, MessageBarType,
} from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { findParentElementWithClass } from '@bingads-webui-campaign-react/asset-library-model';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { addCropSettingsToCreativeImage, constants } from '@bingads-webui-campaign-react/image-experience-model';
import { HistoryBanner, TextItem, ButtonContainer, ImageChooser } from '@bingads-webui-campaign-react/asset-library-creative-components';

import { getStyles } from './style';

import { getMaxLengthOfTemplateMeta } from './utils';
import { CreativeColorEditor } from './color-editor/creative-colors-editor';

const maxCreativeCount = 10;

export const CreativeEditorView = ({
  creativeData,
  dataService,
  finalUrl,
  onApplyCreative,
  onGenerateCreative,
  fetchAigcTemplateDefinition,
  i18n,
  isOpen,
  permissions,
  newImageDataObject,
  cid,
  aid,
  oDataConfig,
  onChangeImage,
  assetLibraryConfig,
  setIsOpen,
  onRenderCTA,
  logger,
}) => {
  const { addCreative, getImageById, uploadImageByUrl } = dataService;

  const [creativeList, setCreativeList] = useState([creativeData.creativeMeta]);
  const [currentCreative, setCurrentCreative] = useState(creativeData.creativeMeta);
  const [originalMetaData, setOriginalMetaData] = useState(null);
  const [newTextListObj, setNewTextListObj] = useState(null);
  const [newCTA, setNewCTA] = useState(null);
  const [newImageObject, setNewImageObject] = useState(null);
  const [loadingCreative, setLoadingCreative] = useState(false);
  const [disableGenerate, setDisableGenerate] = useState(true);
  const [disableApply, setDisableApply] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  // Edit Color state
  const [editColorEnabled, setEditColorEnabled] = useState(false);
  const [selectedColors, setSelectedColors] = useState([]);
  const [backgroundColors, setBackgroundColors] = useState([]);
  const [colorEditorId, setColorEditorId] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [templateVersion, setTemplateVersion] = useState(null);
  const [aigcTemplateList, setAigcTemplateList] = useState({});

  const placementModel = useMemo(() => {
    if (creativeData && creativeData.creativeMeta && creativeData.creativeMeta.placement) {
      return creativeData.creativeMeta.placement;
    }

    return '';
  }, [creativeData]);

  const maxLengthInfo = useMemo(() => {
    if (creativeData && creativeData.creativeMeta && creativeData.creativeMeta.template && creativeData.creativeMeta.template.templateId) {
      return getMaxLengthOfTemplateMeta(templateId, templateVersion, aigcTemplateList);
    }

    return {};
  }, [creativeData, templateId, templateVersion, aigcTemplateList]);

  const formateMetaData = useCallback((creativeMeta) => {
    const { template } = creativeMeta;
    const {
      shortHeadline, longHeadline, description, cta, image,
    } = template || {};
    const textListObjInner = {};
    const originalTextListObjInner = {};
    if (shortHeadline && shortHeadline.text && shortHeadline.editable) {
      textListObjInner.shortHeadline = {
        ...shortHeadline,
        maxLength: maxLengthInfo.shortHeadline,
      };
      originalTextListObjInner.shortHeadline = {
        ...shortHeadline,
        maxLength: maxLengthInfo.shortHeadline,
      };
    }
    if (longHeadline && longHeadline.text && longHeadline.editable) {
      textListObjInner.longHeadline = {
        ...longHeadline,
        maxLength: maxLengthInfo.longHeadline,
      };
      originalTextListObjInner.longHeadline = {
        ...longHeadline,
        maxLength: maxLengthInfo.longHeadline,
      };
    }
    if (description && description.text && description.editable) {
      textListObjInner.description = {
        ...description,
        maxLength: maxLengthInfo.description,
      };
      originalTextListObjInner.description = {
        ...description,
        maxLength: maxLengthInfo.description,
      };
    }
    setNewTextListObj(textListObjInner);

    let CTAInner = null;
    if (cta && cta.text && cta.editable) {
      CTAInner = { CallToAction: '', CallToActionValue: cta.text, CallToActionText: cta.text };
      setNewCTA(CTAInner);
    }

    let imageUrlInner = '';
    if (image && image.url) {
      imageUrlInner = image.url;
      setNewImageObject({ url: imageUrlInner, id: '' });
    }

    setOriginalMetaData({
      textListObj: originalTextListObjInner,
      cta: CTAInner,
      imageUrl: imageUrlInner,
    });

    if (template) {
      setTemplateId(template.templateId);
      setSelectedColors(template.color);
      setTemplateVersion(template.version);
    }
  }, [maxLengthInfo]);

  // maybe changed by generate new creative or choose from history list
  const changeCreative = useCallback((creativeMeta) => {
    setCurrentCreative(creativeMeta);
    // update meta data when current creative changes
    formateMetaData(creativeMeta);
    setDisableGenerate(true);
    setDisableApply(false);
  }, [formateMetaData]);

  // update meta data when current creative changes
  const whenNewCreativeGenerated = useCallback((newCreative) => {
    setCreativeList((prevList) => {
      if (prevList.length === maxCreativeCount) {
        return ([...prevList.slice(1), newCreative]);
      }
      return ([...prevList, newCreative]);
    });
    changeCreative(newCreative);
  }, [changeCreative]);

  const formatRequestCreative = useCallback(() => {
    const textListObj = mapObject(newTextListObj, value => ({
      text: value.text,
    }));
    const color = selectedColors && selectedColors.length > 0 ? selectedColors : [];
    const background = currentCreative.template.background || {};
    const templateMeta = extend({
      image: {
        url: newImageObject.url,
      },
      ...textListObj,
    }, newCTA ? { cta: { text: newCTA.CallToActionText || newCTA.CallToActionValue } } : {});
    const creativeMeta = {
      templateId: currentCreative.template.templateId,
      style: currentCreative.template.style,
      templateName: currentCreative.template.templateName,
      version: currentCreative.template.version,
      ...templateMeta,
    };

    if (templateVersion && Number(templateVersion) > 1.0) {
      creativeMeta.color = color;
      creativeMeta.background = background;
    }

    return creativeMeta;
  }, [currentCreative, newCTA, newImageObject, newTextListObj, selectedColors, templateVersion]);

  const uploadOriginalImage = useCallback(() => new Promise((resolve) => {
    if (newImageObject.url !== originalMetaData.imageUrl) {
      // image already saved in asset library, don't need to upload again
      resolve(newImageObject);
    } else {
      // upload image to asset library, then replace the request creative metadata with saved image url
      uploadImageByUrl({
        imageUrl: newImageObject.url,
        sourceType: constants.sourceTypes.WebSiteImported,
      }).then(imageId => getImageById({ imageId }))
        .then((imageObj) => {
          resolve(imageObj);
        });
    }
  }), [getImageById, newImageObject, originalMetaData, uploadImageByUrl]);

  const handleGenerateCreative = useCallback(() => {
    setLoadingCreative(true);
    setGenerateError('');
    setDisableGenerate(true);
    const creativeMeta = formatRequestCreative();
    onGenerateCreative(creativeMeta)
      .then((res) => {
        whenNewCreativeGenerated(res);
        setLoadingCreative(false);
        logger.logTrace('Creative Editor: creative generated successfully');
      }).catch((err) => {
        setLoadingCreative(false);
        const { errors } = err;
        if (errors && errors[0] && errors[0].message) {
          setGenerateError(errors[0].message);
        } else {
          setGenerateError(i18n.getString(_TL_('Failed to generate new creative')));
        }
      });
  }, [formatRequestCreative, onGenerateCreative, whenNewCreativeGenerated, logger, i18n]);

  const updateTextList = useCallback((key, value) => {
    const newTextListCopy = { ...newTextListObj };
    newTextListCopy[key].text = value;
    setNewTextListObj(newTextListCopy);
  }, [newTextListObj]);

  const handleAddCreative = useCallback(
    (creative, image) => addCreative({ creativeData: creative, baseId: image.id }) // save original image id as creative's baseId, then the original can not be deleted
      .then((ids) => {
        if (isObject(first(ids))) {
          throw new Error(ids[0].code);
        }

        return ids[0];
      })
      .then(id => getImageById({ imageId: id }))
      .then(mtObj => addCropSettingsToCreativeImage(mtObj)),
    [addCreative, getImageById]
  );

  const handleApplyCreative = useCallback(() => {
    if (isEqual(currentCreative, creativeData.creativeMeta)) {
      setIsOpen(false);
    } else {
      let creativeWithSavedOriginalImage = currentCreative;
      setDisableApply(true);
      // save original image and update creative with saved image url
      uploadOriginalImage()
        .then((image) => {
          creativeWithSavedOriginalImage = {
            ...currentCreative,
            template: {
              ...currentCreative.template,
              image: {
                ...currentCreative.template.image,
                url: image.url,
              },
            },
          };
          return handleAddCreative(creativeWithSavedOriginalImage, image);
        })
        .then((imageObj) => {
          onApplyCreative({
            imageObj,
            creativeMeta: {
              placement: placementModel,
              ...creativeWithSavedOriginalImage,
            },
          });
          setIsOpen(false);
        });
    }
    logger.logTrace('Creative Editor: creative applied');
  }, [currentCreative, creativeData.creativeMeta, logger, setIsOpen, uploadOriginalImage, handleAddCreative, onApplyCreative, placementModel]);

  useEffect(() => {
    changeCreative(creativeData.creativeMeta);
    logger.logTrace('Creative Editor: creative editor opened');
  }, [creativeData.creativeMeta]);

  useEffect(() => {
    if (newImageDataObject && newImageDataObject.url) {
      setNewImageObject(newImageDataObject);
    }
  }, [newImageDataObject]);

  useEffect(() => {
    // disable generate button when no meta data changes
    let isCTAChanged = false;
    if (newCTA && originalMetaData && originalMetaData.cta) {
      // CallToActionText is used for custom CTA
      if (newCTA.CallToActionText) {
        isCTAChanged = newCTA.CallToActionText !== originalMetaData.cta.CallToActionText;
      } else {
        isCTAChanged = newCTA.CallToActionValue !== originalMetaData.cta.CallToActionValue;
      }
    }

    let hasInvalidText = false;
    keys(newTextListObj).map((key) => {
      const textObj = newTextListObj[key];
      if (textObj.text.length > textObj.maxLength || textObj.text.length <= 0) {
        hasInvalidText = true;
      }
      return null;
    });

    const imageChanged = newImageObject && originalMetaData && newImageObject.url !== originalMetaData.imageUrl;
    const textChanged = originalMetaData && !isEqual(originalMetaData.textListObj, newTextListObj);
    const metaDataChanged = imageChanged || textChanged || isCTAChanged;

    if (originalMetaData && !hasInvalidText && metaDataChanged && !imageLoadFailed) {
      setDisableGenerate(false);
      setDisableApply(true);
    } else if (originalMetaData) {
      setDisableGenerate(true);
      setDisableApply(false);
    }
  }, [imageLoadFailed, newCTA, newImageObject, newTextListObj, originalMetaData]);

  const classes = useDefaultStyles(getStyles);

  const onDismiss = useCallback((evt) => {
    const target = evt && evt.currentTarget;
    const panelElem = findParentElementWithClass(target, classes.creativeEditor);
    if (panelElem || evt.key === 'Escape') {
      setIsOpen(false);
      logger.logTrace('Creative Editor: panel closed');
    }
  }, [classes.creativeEditor, logger, setIsOpen]);

  const onRenderCTAInner = useCallback(() => (onRenderCTA ? onRenderCTA(newCTA, setNewCTA) : null), [onRenderCTA, newCTA]);

  const updateSelectedColors = (color) => {
    const newColors = selectedColors.reduce((acc, curr) => {
      if (curr.elementName === color.elementName) {
        acc.push(color);
      } else {
        acc.push(curr);
      }
      return acc;
    }, []);

    setDisableGenerate(false);
    setSelectedColors(newColors);
  };

  useEffect(() => {
    const editColor = templateVersion && Number(templateVersion) > 1.0 &&
    selectedColors && selectedColors.length > 0;
    setEditColorEnabled(editColor);
  }, [selectedColors, templateVersion]);

  useEffect(() => {
    async function getTemplate() {
      if (templateId) {
        const templateResults = await fetchAigcTemplateDefinition(templateId);
        const templates = templateResults && templateResults.Data && templateResults.Data.displayAdsTemplateDefinitions ? templateResults.Data.displayAdsTemplateDefinitions : null;
        setAigcTemplateList(templates);
      }
    }
    getTemplate();
  }, [templateId, fetchAigcTemplateDefinition]);

  useEffect(() => {
    const bgColors = selectedColors && selectedColors.length > 0 ? selectedColors.filter(color => color.elementName.toLowerCase().includes('background')) : [];
    setBackgroundColors(bgColors);
  }, [selectedColors]);

  return (
    <Panel
      className={classNames('asset-library-creative-editor-view', classes.creativeEditor)}
      headerText={i18n.getString(_TL_('Edit your creative'))}
      isOpen={isOpen}
      type={PanelType.custom}
      onDismiss={onDismiss}
      customWidth="1224px"
      layerProps={{
        styles: {
          root: {
            zIndex: 900,
          },
        },
      }}
      styles={{
        root: {
          top: 60,
        },
        main: {
          overflowY: 'hidden',
        },
      }}
    >
      <div className="left-panel">
        <div className="panel-description">{i18n.getString(_TL_('Fine-tune your AI-generated creative to make new variations.'))}</div>
        {
          keys(newTextListObj).map((key, index) => {
            let errorMessage = '';
            if (newTextListObj[key].text.length > newTextListObj[key].maxLength) {
              errorMessage = i18n.getString(_TL_('Value cannot be longer than {{maxLength}} characters'), { maxLength: newTextListObj[key].maxLength });
            } else if (newTextListObj[key].text.length <= 0) {
              errorMessage = i18n.getString(_TL_('Required field'));
            }

            let colorData;
            if (editColorEnabled) {
              colorData = selectedColors.find(color => color.elementName.toLowerCase() === key.toLowerCase());
            }

            const label = key.split(/(?=[A-Z])/)
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ') || `Text ${index + 1}`;

            return (
              <div key={key} className="text-item">
                <TextItem
                  text={newTextListObj[key].text}
                  updateText={str => updateTextList(key, str)}
                  title={i18n.getString(_TL_('{{label}}'), { label })}
                  errorMessage={errorMessage}
                  showHelpPopup
                  helpTopic="pop_BA_DisplayAds_Text"
                />
                {(permissions && permissions.dynamic && permissions.dynamic.AigcDisplayAdColorEdit) && colorData &&
                  <CreativeColorEditor
                    colors={[colorData]}
                    updateSelectedColors={updateSelectedColors}
                    colorEditorId={colorEditorId}
                    setColorEditorId={setColorEditorId}
                    logger={logger}
                  />
                }
              </div>
            );
          })
        }
        {
          originalMetaData && originalMetaData.cta &&
          <div className="text-container">
            <div className="item-title">
              <div className="text-title">{i18n.getString(_TL_('Call to action'))}</div>
              <HelpPopupInline topic="pop_BA_DisplayAds_CTA" />
            </div>
            <div className="text-field" key={originalMetaData.cta.CallToActionValue}>{onRenderCTAInner()}</div>
          </div>
        }
        <ImageChooser
          i18n={i18n}
          currentImage={newImageObject && newImageObject.url}
          cid={cid}
          aid={aid}
          oDataConfig={oDataConfig}
          finalUrl={finalUrl}
          onChangeImage={onChangeImage}
          assetLibraryConfig={assetLibraryConfig}
          showHelpPopup
          helpTopic="pop_BA_DisplayAds_GeneratedImage"
          onLoadingImage={(success) => { setImageLoadFailed(!success); }}
        />
        {(permissions && permissions.dynamic && permissions.dynamic.AigcDisplayAdColorEdit) &&
             editColorEnabled && backgroundColors.length > 0 && (
            <>
              <div className="text-container">
                <div className="item-title">
                  <div className="text-title">{i18n.getString(_TL_('Background colors'))}</div>
                  <HelpPopupInline topic="pop_BA_DisplayAds_Color" />
                </div>
                <CreativeColorEditor
                  colors={backgroundColors}
                  updateSelectedColors={updateSelectedColors}
                  colorEditorId={colorEditorId}
                  setColorEditorId={setColorEditorId}
                  logger={logger}
                />
              </div>
            </>
        )}
        <DefaultButton
          iconProps={{ iconName: 'Send' }}
          onClick={handleGenerateCreative}
          className="generate-creative-button"
          text={i18n.getString(_TL_('Generate'))}
          disabled={disableGenerate}
        />
      </div>
      <div className="preview-container">
        <div className="preivew-box">
          {
            loadingCreative ?
              <div className="loading-box">
                <div className="loading-placeholder" />
                <div className="spinner-box">
                  <Spinner />
                  <span className="spinner-text">{i18n.getString(_TL_('Generating a new creative...'))}</span>
                </div>
              </div> :
              <div className="preview-image">
                { currentCreative && currentCreative.url && <img src={currentCreative.url} alt="creative" /> }
              </div>
          }
          <div className="message-bar">
            {
            generateError &&
              <MessageBar
                messageBarType={MessageBarType.error}
                isMultiline={false}
                onDismiss={() => setGenerateError(undefined)}
                dismissButtonAriaLabel="Close"
              >
                {generateError}
              </MessageBar>
            }
          </div>
        </div>
        <HistoryBanner creativeList={creativeList} switchCreative={changeCreative} />
      </div>
      <ButtonContainer
        i18n={i18n}
        handleApply={handleApplyCreative}
        handleCancel={() => {
          setIsOpen(false);
          logger.logTrace('Creative Editor: cancel button clicked');
        }}
        disableApply={disableApply}
      />
    </Panel>
  );
};

CreativeEditorView.propTypes = {
  creativeData: PropTypes.shape({
    creativeId: PropTypes.string,
    creativeMeta: PropTypes.shape({
      template: PropTypes.shape({
        templateId: PropTypes.string.isRequired,
        image: PropTypes.shape({
          url: PropTypes.string.isRequired,
        }).isRequired,
        cta: PropTypes.shape({
          text: PropTypes.string,
          editable: PropTypes.bool,
        }),
      }).isRequired,
      placement: PropTypes.string,
    }).isRequired,
  }).isRequired,
  dataService: PropTypes.shape({
    addCreative: PropTypes.func.isRequired,
    getImageById: PropTypes.func.isRequired,
    uploadImageByUrl: PropTypes.func.isRequired,
  }).isRequired,
  onApplyCreative: PropTypes.func.isRequired,
  onGenerateCreative: PropTypes.func.isRequired,
  fetchAigcTemplateDefinition: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      AigcDisplayAdColorEdit: PropTypes.bool,
    }),
  }),
  onChangeImage: PropTypes.func.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  onRenderCTA: PropTypes.func,
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
  oDataConfig: PropTypes.shape({
    odataRootUrl: PropTypes.string,
    odataPath: PropTypes.string,
    odataToken: PropTypes.string,
  }).isRequired,
  finalUrl: PropTypes.string,
  assetLibraryConfig: PropTypes.shape({}).isRequired,
  newImageDataObject: PropTypes.shape({
    url: PropTypes.string,
  }),
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
};

CreativeEditorView.defaultProps = {
  onRenderCTA: noop,
  finalUrl: '',
  newImageDataObject: null,
  permissions: null,
};
